import React, { Component } from "react";
import { Grid, Loader, Pagination, Panel, SearchBar, Table, Warning } from "@culturehq/components";
import client from "@culturehq/client";

import { useFieldsLabels, useUserAutocomplete } from "app/hooks";
import { t, Trans } from "app/utils";

import InviteModal from "components/admin/InviteModal";
import ErrorMessage from "components/common/ErrorMessage";
import Dropdown from "components/display/Dropdown";

import UserRow from "./users/UserRow";

const UserAutocomplete = ({ labels, options, onResults, page }) => {
  const handleSearch = useUserAutocomplete(options, onResults, page);

  return (
    <>
      <label className="search-users" htmlFor="search"><Trans>Search Users: </Trans></label>
      <SearchBar
        onSearch={handleSearch}
        placeholder={
          t(`Search by Name, ${labels.departments.plural}, Interests, etc.`)
        }
      />
    </>
  );
};

const FILTERS = [
  {
    value: { includeDeactivated: true },
    label: t("See All Users"),
    title: t("Total Users")
  },
  {
    value: { includeDeactivated: true, active: true },
    label: t("Enabled"),
    title: t("Total Enabled")
  },
  {
    value: { includeDeactivated: true, active: false },
    label: t("Disabled"),
    title: t("Total Disabled")
  },
  {
    value: { includeDeactivated: true, inviteStatus: "expired", unapproved: false },
    label: t("Invite Expired"),
    title: t("Total Invites Expired")
  },
  {
    value: { includeDeactivated: true, inviteStatus: "pending", unapproved: false },
    label: t("Invite Pending"),
    title: t("Total Invited Pending")
  },
  {
    value: { includeDeactivated: true, unapproved: true },
    label: t("Unapproved"),
    title: t("Unapproved and pending")
  }
];

class UsersContext extends Component {
  state = {
    error: false,
    loading: true,
    pagination: {},
    users: [],
    filters: FILTERS[0],
    query: null,
    page: 1
  };

  componentDidMount() {
    this.fetchUsers();
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, page, query } = this.state;

    if (
      filters !== prevState.filters
      || (page !== prevState.page && !query)
      || (query !== prevState.query && !query)
    ) {
      this.fetchUsers();
    }
  }

  handleFilterSelected = filters => {
    this.setState({ filters, page: 1 });
  };

  handleResults = ({ users, pagination, query }) => {
    this.setState({ users, pagination, query });
  };

  handlePageSelected = page => {
    this.setState({ page });
  };

  handleUpdate = updatedUser => {
    this.setState(({ users }) => ({
      users: users.map(user => (user.id === updatedUser.id ? updatedUser : user))
    }));
  };

  handleDelete = deletedUser => {
    this.setState(({ users }) => ({
      users: users.filter(({ id }) => id !== deletedUser.id)
    }));
  };

  async fetchUsers() {
    const { filters, page } = this.state;

    this.setState({ loading: true });

    try {
      const { users, pagination } = await client.listUsers({ ...filters.value, page });

      this.setState({ loading: false, error: false, users, pagination });
    } catch (error) {
      this.setState({ loading: false, error });
    }
  }

  render() {
    const { users, filters, loading, pagination, error, query, page } = this.state;
    const { labels } = this.props;

    return (
      <Panel limitWidth>
        <Panel.Heading primary>Users</Panel.Heading>
        <Panel.Body className="ManageUsers">
          <div className="score-users">
            <InviteModal />
            <h2>
              {filters.title}
              {query && t(` Matching "${query}"`)}: {pagination.totalCount}
            </h2>
          </div>
          <div>
            <Grid className="grid-items">
              <Grid.Item sm={8}>
                <UserAutocomplete
                  labels={labels}
                  options={filters.value}
                  onResults={this.handleResults}
                  page={page}
                />
              </Grid.Item>
              <Grid.Item sm={4} className="grid-item-filter">
                <p className="dropdown-label">
                  <Trans>Filter</Trans>
                </p>
                <Dropdown
                  options={FILTERS}
                  current={filters}
                  onChange={this.handleFilterSelected}
                />
              </Grid.Item>
            </Grid>
          </div>
          {error && <ErrorMessage error={error} />}

          <Loader loading={loading}>
            <Table className="no-scrollbar">
              <thead>
                <tr>
                  <th />
                  <th><Trans>Name</Trans></th>
                  <th><Trans>Stories</Trans></th>
                  <th>
                    <Trans>{labels.departments.plural}</Trans>
                  </th>
                  <th><Trans>Location</Trans></th>
                  <th><Trans>Status</Trans></th>
                  <th className="actions">
                    <Trans>Actions</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <UserRow
                    key={user.id}
                    user={user}
                    onUpdate={this.handleUpdate}
                    onDelete={this.handleDelete}
                  />
                ))}
              </tbody>
            </Table>

            {users.length < 1 && (
              <Warning><Trans>No results to show.</Trans></Warning>
            )}
            <Pagination {...pagination} onClick={this.handlePageSelected} />
          </Loader>
        </Panel.Body>
      </Panel>
    );
  }
}

const Users = props => {
  const labels = useFieldsLabels();

  return (
    <UsersContext
      labels={labels}
      {...props}
    />
  );
};

export default Users;
